import { IDataProvider } from "motw_core/Data/IDataProvider";
import { IGoal, resourceNameGoal } from "motw_core/Entity/IGoal";
import {
    IGoalDataPoint,
    resourceNameGoalDataPoint,
} from "motw_core/Entity/IGoalDataPoint";
import { ILogEntry } from "motw_core/Entity/ILogEntry";
import { EGoalDatapointType } from "motw_core/Goals/GoalDataPoints/EGoalDatapointType";
import * as React from "react";
import { HandleError } from "source/app/presentation/Shared/HandleError";
import ClientDataProvider from "source/app/services/External/ClientDataProvider/ClientDataProvider";
import { BannerImages } from "source/app/services/Shared/RenderSwal/BannerImage/BannerImage";
import { Sounds } from "source/app/services/Sound/Sounds";
import { ETexture } from "source/app/services/UI/Background/Texture/ETexture";
import { HSLColors } from "source/app/services/UI/Background/Texture/Helpers/HSLColor";
import { Texture } from "source/app/services/UI/Background/Texture/Texture";
import * as toastr from "toastr";
import { goalsService } from "../../../../../../../motw_core/Services/GoalsService";
import Data from "../../../../services/Data";
import RenderSwal, {
    TSwalOptions,
} from "../../../../services/Shared/RenderSwal/RenderSwal";
import { AddStatusMessage } from "../../StatusBar/StatusBar";
import { EStatusMessageType } from "../../StatusBar/StatusMessageType/StatusMessageType";
import { isNewGoal } from "../GoalHelpers/isNewGoal";
import { IGoalsContainerState } from "../IGoalsState";
import { GetGEPState } from "./GetGEPState";
import { GEPStateSnapshot } from "./GoalEditorPanel/GEPState/GEPStateSnapshot";
import { Validate } from "./GoalEditorPanel/GEPState/Validate";
import GoalEditorPanel from "./GoalEditorPanel/GoalEditorPanel";
import { PrepareForDbAdd } from "./PrepareForPersistence";
const moment = require("moment");

export const GoalEditor = async (goal: IGoal) => {
    Sounds.shoutYeah.Play();
    Sounds.kickdrum_heavy.Play();

    const isNew = isNewGoal(goal);

    const swalOptions: TSwalOptions = {
        title: isNew ? `Begin a new goal` : `Edit goal "${goal.name}"`,
        preConfirm: () => {
            const GEPState = GetGEPState().currentState;
            const error = Validate(GEPState);
            if (error) {
                toastr.error(error);
                return false;
            }
        },
        bannerImage: BannerImages.target,
        background: new Texture({
            image: ETexture["dark-wood"],
            backgroundColor: HSLColors.transparent,
        }).combinedCSS.background as string,
    };

    const AddGoalAction = async (willSave: boolean) => {
        const GEPState: GEPStateSnapshot = GetGEPState();
        PrepareForDbAdd(GEPState);

        if (willSave) {
            await ClientDataProvider.AddRecordAsync(
                resourceNameGoal,
                GEPState.currentState
            );

            const isRateUnchanged =
                GEPState.currentState.weeklyrate ===
                GEPState.previousState.weeklyrate;

            try {
                await goalsService.AddWeeklyRateChangeDataPointAsync(
                    isRateUnchanged,
                    GEPState.currentState,
                    ClientDataProvider
                );
            } catch (e) {
                HandleError(e);
            }

            Data.Load.Single.ByStateTarget<IGoalsContainerState>("Goals");

            Data.Load.Single.ByStateTarget<IGoalsContainerState>(
                "GoalDataPoints"
            );
        }
    };

    const UpdateGoalAction = async (willSave: boolean) => {
        const GEPState: GEPStateSnapshot = GetGEPState();

        if (willSave) {
            const shouldAddWeeklyRateChangeDataPoint =
                GEPState.currentState.weeklyrate ===
                GEPState.previousState.weeklyrate;

            await goalsService.UpdateGoalAsync(
                ClientDataProvider,
                GEPState.currentState,
                shouldAddWeeklyRateChangeDataPoint
            );

            Data.Load.Single.ByStateTarget<IGoalsContainerState>("Goals");

            Data.Load.Single.ByStateTarget<IGoalsContainerState>(
                "GoalDataPoints"
            );
        }
    };

    RenderSwal(
        swalOptions,
        <GoalEditorPanel goal={goal} isNewGoal={isNew} />,
        isNew ? AddGoalAction : UpdateGoalAction,
        () => {}
    );
};

export async function UpdateDatapointFromLogEntryAsync(
    logEntryRow: ILogEntry,
    dataProvider: IDataProvider
) {
    const entryText = logEntryRow.entry;

    const matchingGoal = await goalsService.GetMatchingGoalAsync(
        entryText,
        dataProvider
    );

    const datapointId = logEntryRow.goaldatapointid;

    if (matchingGoal && datapointId) {
        const entryWords = entryText.split(" ");
        const value = +entryWords[3]; // The plus sign casts it the word as a number, and returns NaN if it fails
        const comment = entryWords.splice(4).join(" ") || "-";

        const newDatapoint: IGoalDataPoint = {
            id: datapointId,
            goalid: matchingGoal.id,
            value: value,
            comment: comment,
            egoaldatapointtype: EGoalDatapointType.GoalDatapoint,
            lastmodifiedtime: moment().format(),
        };

        try {
            dataProvider
                .UpdateRecordAsync(
                    `${resourceNameGoalDataPoint}?id=eq.${datapointId}`,
                    newDatapoint
                )
                .catch(HandleError);

            AddStatusMessage(
                `Update datapoint ${datapointId} for the goal ${matchingGoal.name}`,
                EStatusMessageType.success
            );
        } catch (e) {
            HandleError(e);
        }
    }
}
